import isEqual from 'deep-equal';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useConfig } from 'shared/config/ConfigContext';
import { ResultPageMapLayout } from 'shared/page/map/ResultPageMapLayout';
import type { DeviceType } from 'shared/types';
import type { FhhState, FhhUnleashToggles, Filters } from '@/domain';
import * as resultActions from '@/ducks/results/resultsActions';
import { trackResultPage } from '@/lib/tracking/tracking';
import type { LoginId } from '@/types/common';
import { Filter } from './components/filters/Filter';
import { MapCard } from './components/mapCard/MapCard';
import { ResultListMap } from './components/resultListMap/ResultListMap';
import { parseUrl } from './utils/parseUrl';

import 'shared/map/map.css';

interface ResultMapState {
    isMobile: boolean;
    filtersOpen: boolean;
    selectedAdId: string | null;
    windowSize: number[];
}

interface ResultMapProps {
    deviceType: DeviceType;
    loginId?: LoginId;
    unleash?: FhhUnleashToggles;
}

export const ResultMap = ({ deviceType, loginId }: ResultMapProps) => {
    const { apiExternalUrl } = useConfig();
    const dispatch = useDispatch();
    const router = useRouter();
    const { data, filters, selectedAd, filterData } = useSelector((state: FhhState) => state.results, shallowEqual);
    const [state, setState] = useState<ResultMapState>({
        isMobile: !!deviceType.isProbablyMobile,
        filtersOpen: false,
        selectedAdId: null,
        windowSize: [],
    });
    const [isClient, setIsClient] = useState(false);
    const [poiClickId, setpoiClickId] = useState<string | null>(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const prevFiltersRef = useRef<Filters>(filters);
    const prevDataStatsRef = useRef(data.stats);
    const hasInitiallyPopulatedMapResults = useRef(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        if (data.resultItems !== null) {
            setIsInitialLoad(false);
        }
    }, [data.resultItems]);

    useEffect(() => {
        if (!hasInitiallyPopulatedMapResults.current && isClient) {
            const query = router?.query || {};
            const mapFilters = Object.assign({}, filters, query);

            if (data.listMap === null) {
                dispatch(resultActions.populateMapResults(apiExternalUrl, mapFilters));
            }
            if (data.resultItems === null) {
                dispatch(resultActions.populateResults(apiExternalUrl, mapFilters));
            }

            hasInitiallyPopulatedMapResults.current = true;
        }
    }, [apiExternalUrl, filters, router?.query, data.listMap, data.resultItems, dispatch, isClient]);

    const toggleFilterState = useCallback(() => {
        setState((prev) => ({
            ...prev,
            filtersOpen: !prev.filtersOpen,
        }));
    }, []);

    const handleResize = useCallback(() => {
        if (typeof window !== 'undefined') {
            setState((prev) => ({
                ...prev,
                windowSize: [window.innerWidth, window.innerHeight],
            }));
        }
    }, []);

    const populateResultsIfEmpty = useCallback(() => {
        const query = router?.query || {};

        if (data.resultItems === null) {
            dispatch(resultActions.populateResults(apiExternalUrl, Object.assign({}, filters, query)));
        }
    }, [router?.query, data.resultItems, filters, dispatch]);

    const isFalseyOrEmpty = useCallback(
        (value: unknown): boolean =>
            !value || (Object.prototype.toString.call(value) === '[object Object]' && JSON.stringify(value) === '{}'),
        [],
    );

    useEffect(() => {
        if (state.windowSize.length > 0) {
            let rowCoefficient = 1;
            if (!state.isMobile) {
                rowCoefficient = 6;
            }
            const rows = Math.floor(state.windowSize[1] / 200) + rowCoefficient;
            const columns = Math.floor(state.windowSize[0] / 200) + 4;
            dispatch(resultActions.updateRowsAndColumnsForMap(rows, columns));
        }
        populateResultsIfEmpty();
        dispatch(resultActions.setNumberOfFusAdsMapDefault(filters));
        if (!isFalseyOrEmpty(router?.query)) {
            dispatch(resultActions.updateFilters(router.query as Partial<Filters>));
        }
        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const filtersChanged = !isEqual(filters, prevFiltersRef.current);
        const statsChanged = !isEqual(data.stats, prevDataStatsRef.current);

        if (filtersChanged) {
            router?.replace({ pathname: router.pathname, query: filters }, undefined, { shallow: true });
        }

        // On a page load, the stats object is empty. This object is needed for tracking, so must wait until
        // next update when stats is set before tracking. After that, track on every filter change.
        if (statsChanged || filtersChanged) {
            if (data.stats?.numResults !== undefined) {
                // Pulse tracker id: FHH_pv08 (see tracking section in README.md)
                trackResultPage('Map', { data, filters });
            }
        }

        prevFiltersRef.current = filters;
        prevDataStatsRef.current = data.stats;
    }, [filters, data.stats, router]);

    const searchParams = parseUrl(router.query);
    const boundFilterResults = useCallback((filter: Filters) => dispatch(resultActions.filterResults(apiExternalUrl, filter)), [dispatch]);
    const updateMapFilters = (bounds: { sw: [number, number]; ne: [number, number] }) => {
        boundFilterResults({
            lat_sw: String(bounds.sw[1]),
            lng_sw: String(bounds.sw[0]),
            lat_ne: String(bounds.ne[1]),
            lng_ne: String(bounds.ne[0]),
        });
        dispatch(
            resultActions.populateMapResults(apiExternalUrl, {
                ...filters,
                lat_sw: String(bounds.sw[1]),
                lng_sw: String(bounds.sw[0]),
                lat_ne: String(bounds.ne[1]),
                lng_ne: String(bounds.ne[0]),
            }),
        );
    };

    const onPoiClick = (adId: string) => {
        setpoiClickId(adId);
        dispatch(resultActions.populateMapObject(adId));
    };

    const displayClientSideClusters = data?.stats?.numResults ? +data?.stats?.numResults < 5000 : false;
    const mapData = data?.listMap || data?.resultItems || [];

    let primaryPOI = displayClientSideClusters
        ? mapData?.map((item) => ({
              id: item.id,
              coordinates: [item.longitude, item.latitude],
              name: item.heading,
          }))
        : [];

    const serverSideClusters = displayClientSideClusters
        ? []
        : data?.heatmap?.map((heatmap) => ({
              id: heatmap.id,
              name: heatmap.numberOfEntries,
              coordinates: [heatmap.longitude, heatmap.latitude],
              count: Number.parseInt(heatmap.numberOfEntries.replace('+', '')),
          }));

    const selectedId = state.selectedAdId ?? selectedAd?.id?.toString();
    const selected = primaryPOI?.find((ad) => selectedId === ad.id);
    const selectedPois = selected ? [selected] : [];
    primaryPOI = selected ? primaryPOI?.filter((ad) => ad.id !== selectedId) : primaryPOI;

    return (
        <ResultPageMapLayout
            showFilters={state.filtersOpen}
            toggleFilters={toggleFilterState}
            resultList={
                <ResultListMap
                    results={data.resultItems || null}
                    stats={data.stats as Record<string, unknown>}
                    filters={filters}
                    onFilterChanged={boundFilterResults}
                    numberOfPreloadedImages={6}
                    loginId={loginId}
                    loading={isInitialLoad}
                />
            }
            filters={<Filter onFilterChanged={boundFilterResults} data={filterData} filters={filters} showMap={false} contentOnly />}
            closeMapUrl={`${router.basePath}/resultat/?${searchParams.toString()}`}
            id="result-list-map"
            className="result-list-map"
            // @ts-expect-error
            selectedPois={selectedPois}
            onUpdateBounds={updateMapFilters}
            // @ts-expect-error
            primaryPois={primaryPOI}
            onPinClick={onPoiClick}
            // @ts-expect-error
            serverSideClusters={serverSideClusters}
            hasClustering
            // @ts-expect-error gps stuff is possibly undefined, but it's ok...
            initialBounds={{ sw: [+filters.lng_sw, +filters.lat_sw], ne: [+filters.lng_ne, +filters.lat_ne] }}>
            {poiClickId && selectedAd && (
                <div className="maplibregl-ctrl-bottom-center">
                    <MapCard closeCard={() => setpoiClickId(null)} selectedAd={selectedAd} />
                </div>
            )}
        </ResultPageMapLayout>
    );
};
